"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import CheckboxField from "@/components/CheckboxField";
import ResultCard from "@/components/ResultCard";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";
import Icon from "@/components/Icon";
import { calcKitchen, type KitchenResult, type KitchenTier, KITCHEN_NKBA_PCT } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";

const TIER_OPTIONS = [
  { value: "cosmetic",  label: "Cosmetic Update — paint, hardware, appliance swap" },
  { value: "midrange",  label: "Full Mid-Range — new cabinets, countertops, appliances" },
  { value: "highend",   label: "High-End — custom cabinets, stone counters, pro appliances" },
];

const ROI_BY_TIER: Record<string, number> = {
  cosmetic: 60,
  midrange: 60,
  highend: 55,
};

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function CalculatorClient() {
  const [sqft, setSqft] = useState("");
  const [tier, setTier] = useState<KitchenTier>("midrange");
  const [layoutChange, setLayoutChange] = useState(false);
  const [homeValue, setHomeValue] = useState("");
  const [result, setResult] = useState<KitchenResult | null>(null);

  function handleCalculate() {
    setResult(calcKitchen({
      sqft:        parseFloat(sqft) || 0,
      tier,
      layoutChange,
      homeValue:   parseFloat(homeValue) || undefined,
    }));
  }

  const spec = CALCULATOR_SPECS.kitchen;
  const nkbaPct = Math.round(KITCHEN_NKBA_PCT * 100);

  return (
    <CalculatorShell
      name="Kitchen Remodel Cost Calculator"
      description="Estimate your kitchen renovation budget from a cosmetic refresh to a full high-end remodel, with NKBA budget benchmarks."
      icon="kitchen"
      category="high-value"
      accentColor="#f59e0b"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Kitchen costs vary widely by region and material choices. Cabinet lead times can be 6–10 weeks for custom orders. Always get 3+ contractor quotes before committing."
      inputsPanel={
        <div className="space-y-4">
          <InputField id="kitchen-sqft" label="Kitchen Size" unit="sq ft" type="number" min="0" step="5" placeholder="160" value={sqft} onChange={(e) => { setSqft(e.target.value); setResult(null); }} hint={`US avg kitchen: ${spec.buyingGuide.avgKitchenSizeSqFt.value} sq ft (NKBA 2025)`} />
          <SelectField id="kitchen-tier" label="Remodel Scope" value={tier} onChange={(e) => { setTier(e.target.value as KitchenTier); setResult(null); }} options={TIER_OPTIONS} />
          <InputField id="home-value" label="Home Value (optional)" unit="$" type="number" min="0" step="5000" placeholder="400000" value={homeValue} onChange={(e) => { setHomeValue(e.target.value); setResult(null); }} hint={`NKBA recommends spending ${spec.buyingGuide.nkbaBudgetRule.value} on a kitchen remodel`} />
          <CheckboxField id="layout-change" label="Moving or adding walls / plumbing" hint="Layout changes add $3,000–$10,000+ for structural and mechanical work" checked={layoutChange} onChange={(v) => { setLayoutChange(v); setResult(null); }} />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <div className="py-5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: "var(--ink-faint)" }}>Total Cost Range</p>
              <p className="text-[2.6rem] leading-none font-black tracking-tight mb-2" style={{ color: "var(--ink)" }}>{fmt(result.totalMin)} – {fmt(result.totalMax)}</p>
              <p className="text-[11px] font-mono" style={{ color: "var(--ink-faint)" }}>midpoint {fmt(result.totalMid)} · ${result.costPerSqFt}/sq ft avg</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Resale ROI" value={`~${ROI_BY_TIER[tier]}%`} highlight sub="Cost recovered (NAR 2025)" />
              <ResultCard label="Joy Score" value="10/10" accent sub="Homeowner satisfaction" />
            </div>
            {result.nkbaReasonable === false && result.nkbaMaxBudget && (
              <div className="disclaimer-box flex items-start gap-2">
                <Icon name="warning" className="w-4 h-4 mt-0.5 shrink-0" />
                <span>This budget exceeds {nkbaPct}% of your home&apos;s value ({fmt(result.nkbaMaxBudget)}) — NKBA&apos;s over-improvement threshold. Consider scaling back to protect your resale position.</span>
              </div>
            )}
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Cost Breakdown (Typical Allocation)
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: `Base remodel cost (${sqft || "?"} sq ft × $${result.costPerSqFt}/sq ft)`, value: fmt(result.totalMid - result.layoutChangeCost) },
                  ...(result.layoutChangeCost > 0 ? [{ label: "Layout / structural changes", value: fmt(result.layoutChangeCost) }] : []),
                  { label: "Midpoint total", value: fmt(result.totalMid), bold: true },
                ].map((row, i) => (
                  <div
                    key={i}
                    className={`flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)] transition-all ${
                      row.bold
                        ? "border-l-3 border-l-[var(--accent)] pl-2 bg-[var(--surface-muted)]/30"
                        : "border-l-3 border-l-transparent"
                    }`}
                  >
                    <span className={`text-[12.5px] font-sans ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink-soft)] font-semibold"}`}>
                      {row.label}
                    </span>
                    <span className={`font-mono text-[13px] ${row.bold ? "text-[var(--accent)] font-extrabold" : "text-[var(--ink)] font-bold"}`}>
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="info-box flex items-start gap-2">
              <Icon name="info" className="w-4 h-4 mt-0.5 shrink-0" />
              <span><strong>IRA Rebate tip:</strong> {spec.buyingGuide.applianceRebatesAvailable.value} for qualifying ENERGY STAR electric appliances — check availability in your state.</span>
            </div>
          </div>
        )
      }
    >
      {/* ── Informational Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Budgeting Your Kitchen Remodel (NKBA Guide)</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            The National Kitchen & Bath Association recommends allocating <strong>{spec.buyingGuide.nkbaBudgetRule.value}</strong> of your home&apos;s current market value for a <Link href="/articles/kitchen-remodel-cost-guide/" className="text-[var(--accent)] hover:underline font-semibold">kitchen renovation</Link> to keep the investment in line with what buyers in your neighborhood expect. The average US residential kitchen is <strong>{spec.buyingGuide.avgKitchenSizeSqFt.value} sq ft</strong> per NKBA&apos;s 2025 Design Trends Report. Budget allocation matters as much as total spend: cabinets and hardware typically consume 25–35% of a kitchen budget, countertops 10–15%, appliances 15–20%, and labor 25–35%. Going custom on cabinets or stone countertops can shift those percentages dramatically upward.
          </p>
        </div>
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/kitchen_buying_guide.webp" alt="Modern kitchen remodel with white shaker cabinets, quartz countertops, and stainless steel appliances" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/kitchen_site_prep.webp" alt="Kitchen under renovation showing plumbing rough-in and electrical panel work before cabinet installation" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">Permits, Planning &amp; the Work Triangle</h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Any kitchen work involving structural changes (removing walls), adding or relocating electrical circuits, or moving plumbing <strong>requires permits</strong> from your local Authority Having Jurisdiction (AHJ). Under NEC 210.8, all outlets within 6 feet of the kitchen sink must be GFCI-protected — a code that applies even to cosmetic remodels if outlets are touched. From a design standpoint, the NKBA&apos;s Kitchen Planning Guideline 2 defines the classic work triangle: <strong>{spec.industryStandards.workTriangleStandard.value}</strong>. This principle ensures efficient movement between the sink, range, and refrigerator and remains a key consideration in layout planning. Planning during the <Link href="/articles/best-time-for-renovations/" className="text-[var(--accent)] hover:underline font-semibold">best season for renovations</Link> will help you get top local labor and protect your <Link href="/articles/renovations-add-resale-value/" className="text-[var(--accent)] hover:underline font-semibold">resale value</Link>.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={spec.industryStandards.workTriangleStandard.standardCode}
        sourceName={spec.industryStandards.workTriangleStandard.sourceName}
        sourceUrl={spec.industryStandards.workTriangleStandard.sourceUrl}
        paragraphs={[
          <p key="p1">
            The kitchen work triangle is one of the most enduring principles in residential kitchen design, originally developed by the University of Illinois School of Architecture in the 1940s. Under <b>{spec.industryStandards.workTriangleStandard.standardCode}</b>, each leg of the triangle between the refrigerator, sink, and cooking surface should be between 4 and 9 feet, with the total perimeter not exceeding <b>26 feet</b>. A triangle that is too compact creates cramped working conditions, while one that is too spread out causes unnecessary walking during food prep.
          </p>,
          <p key="p2">
            NKBA&apos;s 2024 updated guidelines recognize that modern open-concept kitchens frequently serve multiple cooks and accommodate islands — making the single triangle concept less universally applicable. The updated standard introduces &quot;work zones&quot; — dedicated areas for prep, cooking, cleanup, and storage — as a flexible alternative for larger kitchens. When planning your remodel, share your household&apos;s cooking habits with your designer to determine whether a classic triangle or a multi-zone layout best suits your lifestyle.
          </p>
        ]}
        keyRequirements={[
          "Each leg of the kitchen work triangle must measure between 4 and 9 feet.",
          "The total perimeter of the work triangle must not exceed 26 feet.",
          "No major traffic path should intersect the work triangle lines.",
          "Work zones can be utilized as a modern alternative for multi-cook kitchens."
        ]}
      />

      {/* Featured Guide Banner */}
      <div className="my-8 p-5 bg-[var(--surface-muted)] border border-[var(--border)] rounded-lg flex flex-col sm:flex-row items-center justify-between gap-6 card-blueprint text-left">
        <div className="flex-1 flex flex-col gap-2">
          <span className="text-[9px] font-mono text-[var(--accent)] uppercase font-bold tracking-wider">Featured Kitchen Guide</span>
          <h4 className="text-base font-bold text-[var(--ink)] m-0">Kitchen Remodel Cost Guide: Cabinets, Countertops & Labor (2026 Prices)</h4>
          <p className="text-xs text-[var(--ink-soft)] leading-relaxed m-0">
            Compare budgets for cosmetic, mid-range, and luxury kitchen remodels, and learn about the NKBA budget allocation guidelines.
          </p>
        </div>
        <Link 
          href="/articles/kitchen-remodel-cost-guide/" 
          className="px-4 py-2 bg-[var(--accent)] text-white hover:bg-[var(--accent)]/90 text-xs font-mono font-bold uppercase tracking-wider rounded transition-all shrink-0 cursor-pointer text-center"
        >
          Read Kitchen Guide &rarr;
        </Link>
      </div>

      <FaqSection calculatorId="kitchen" />
      <RelatedCalculators calculatorId="kitchen" />
    </CalculatorShell>
  );
}
